import React, { useState, useEffect } from 'react';
import { fetchSubcounties } from '../services/subcountyService';
import { fetchUniqueStreams } from '../services/streamService';
import { saveRevenueTarget } from '../services/revTargetService'; 
import { getFinancialYearData } from '../utils/dateHelper'; 
import { formatWithCommas,stripCommas } from '../utils/fmtNoHelper';
import { Filter } from 'lucide-react';
import { useAuth } from '../context/useAuth';

const months = [
  { key: 'jul_target', label: 'July' }, { key: 'aug_target', label: 'August' }, 
  { key: 'sep_target', label: 'September' }, { key: 'oct_target', label: 'October' }, 
  { key: 'nov_target', label: 'November' }, { key: 'dec_target', label: 'December' },
  { key: 'jan_target', label: 'January' }, { key: 'feb_target', label: 'February' }, 
  { key: 'mar_target', label: 'March' }, { key: 'apr_target', label: 'April' }, 
  { key: 'may_target', label: 'May' }, { key: 'jun_target', label: 'June' }
];

const RevTargetForm = ({ initialData, onSuccess, isEditing }) => {
  const [subcounties, setSubcounties] = useState([]);
  const [streams, setStreams] = useState([]);
  const { user } = useAuth(); // Access user from context
  const fyData = getFinancialYearData();

  const [formData, setFormData] = useState({
    sub_county_id: user?.sub_county_id || '', // Pre-fill if user has sub-county assigned
    rev_stream: '',
    financial_year: fyData.current,
    annual_target: 0,
    ...months.reduce((acc, m) => ({ ...acc, [m.key]: 0 }), {}) // Initialize months
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        annual_target: initialData.annual_target || 0,
        jul_target: initialData.jul_target || 0,
        aug_target: initialData.aug_target || 0,
        sep_target: initialData.sep_target || 0,
        oct_target: initialData.oct_target || 0,
        nov_target: initialData.nov_target || 0,
        dec_target: initialData.dec_target || 0,
        jan_target: initialData.jan_target || 0,
        feb_target: initialData.feb_target || 0,
        mar_target: initialData.mar_target || 0,
        apr_target: initialData.apr_target || 0,
        may_target: initialData.may_target || 0,
        jun_target: initialData.jun_target || 0
      });
    }
    const loadMetadata = async () => {
      const [sc, st] = await Promise.all([fetchSubcounties(), fetchUniqueStreams()]);
       const allowedSubcounties = user?.sub_county_id 
        ? (sc || []).filter(sc => sc.id === user.sub_county_id)
        : (sc || []);

      setSubcounties(allowedSubcounties);
      //setSubcounties(sc);
      setStreams(st);
    };
    loadMetadata();
  }, [initialData, user?.sub_county_id]);

  // Auto-calculate annual total when monthly values change
  //useEffect(() => {
    //const total = months.reduce((sum, m) => sum + Number(formData[m.key] || 0), 0);
    //setFormData(prev => ({ ...prev, annual_target: total }));
  //}, [formData.jul_target, formData.aug_target, formData.sep_target, formData.oct_target, formData.nov_target, formData.dec_target, formData.jan_target, formData.feb_target, formData.mar_target, formData.apr_target, formData.may_target, formData.jun_target, formData]);

  // Calculate total on every render instead of using an effect
  const calculatedAnnualTarget = months.reduce((sum, m) => sum + Number(formData[m.key] || 0), 0);  
  
  // Inside RevTargetForm.jsx handleSubmit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create a clean payload with numeric values
    const payload = {
      sub_county_id: formData.sub_county_id,
      rev_stream: formData.rev_stream,
      financial_year: formData.financial_year,
      annual_target:Number(calculatedAnnualTarget),
      // Explicitly mapping to ensure the backend receives exactly what it needs
      jul_target: Number(formData.jul_target || 0),
      aug_target: Number(formData.aug_target || 0),
      sep_target: Number(formData.sep_target || 0),
      oct_target: Number(formData.oct_target || 0),
      nov_target: Number(formData.nov_target || 0),
      dec_target: Number(formData.dec_target || 0),
      jan_target: Number(formData.jan_target || 0),
      feb_target: Number(formData.feb_target || 0),
      mar_target: Number(formData.mar_target || 0),
      apr_target: Number(formData.apr_target || 0),
      may_target: Number(formData.may_target || 0),
      jun_target: Number(formData.jun_target || 0)
    };

    try {
      await saveRevenueTarget(payload);
      alert("Targets saved successfully!");
      onSuccess();
    } catch (err) {
      console.error("Save Error:", err);
    }
  };

  return (
    <div className="bg-white p-8 rounded-2xl border shadow-sm max-w-5xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Visual indicator that filters are locked */}
      <div className={`bg-white p-6 rounded-xl border border-gray-100 shadow-sm ${isEditing ? 'bg-gray-50 opacity-75' : ''}`}>
        <div className="flex items-center text-gray-700 font-bold border-b pb-2 mb-4">
          <Filter size={20} className={`mr-2 ${isEditing ? 'text-gray-400' : 'text-blue-600'}`} />
          <span>{isEditing ? 'Filters Locked (Editing in Progress)' : 'Search & Filter Targets'}</span>
        </div>
        {/* Horizontal Filters */}
        <div className="flex flex-col md:flex-row gap-2 ">
          {/* Financial Year */}
          <div className="flex-1 space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase">Financial Year</label>
            <select
              className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.financial_year}
              onChange={e => setFormData({...formData, financial_year: e.target.value})}
              required
              disabled={isEditing}
            >
              <option value={fyData.current}>{fyData.current}</option>
              <option value={fyData.next}>{fyData.next}</option>
            </select>
          </div>
          {/* Sub-County */}
          <div className="flex-1 space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase">Sub-County</label>
            <select
              disabled={isEditing} // Disable if editing or if user has a sub-county assigned
              className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.sub_county_id}
              onChange={e => setFormData({...formData, sub_county_id: e.target.value})}

              required
            >

             {!user?.sub_county_id && <option value="">All Sub-Counties</option>}
              {subcounties.map(sc => (
                <option key={sc.id} value={sc.id}>{sc.name}</option>
              ))}
            </select>
          </div>
          {/* Revenue Stream */}
          <div className="flex-1 space-y-2">
            <label className="text-xs font-semibold text-gray-500 uppercase">Revenue Stream</label>
            <select
              disabled={isEditing}
              className="w-full border border-gray-300 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
              value={formData.rev_stream}
              onChange={e => setFormData({...formData, rev_stream: e.target.value})}
              required
            >
              <option value="">Select Stream</option>
              {streams.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

        <div className="bg-blue-50/50 p-6 rounded-2xl border border-blue-100">
          <h4 className="font-bold text-blue-800 mb-6 flex justify-between items-center">
            Monthly Target Allocation
            <span className="text-sm font-normal text-blue-600">Enter amounts for each month</span>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {months.map(m => (
              <div key={m.key} className="space-y-1">
                <label className="text-[10px] font-bold text-gray-500 uppercase">{m.label}</label>
                <input 
                  type="text" // Changed to text to support comma formatting while typing
                  className="w-full p-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  value={formatWithCommas(formData[m.key].toString())}
                  onChange={e => {
                    const rawValue = stripCommas(e.target.value);
                    if (!isNaN(rawValue)) {
                      setFormData({...formData, [m.key]: rawValue});
                    }
                  }}
                />
              </div>
            ))}
          </div>
          <div className="mt-8 pt-6 border-t border-blue-100 flex justify-end items-center space-x-4">
            <span className="text-gray-600 font-medium">Calculated Annual Total:</span>
            <span className="text-2xl font-black text-blue-700">KES {formatWithCommas(calculatedAnnualTarget.toString())}</span>
          </div>
        </div>
          <div className="md:col-span-2 flex space-x-3 pt-4">
            <button type="submit"
              className="flex-1 bg-blue-600 text-white py-3.5 rounded-xl font-bold hover:bg-blue-700 shadow-lg transition-all"
            >
              {initialData ? 'Update Target' : 'Save Target'}
            </button>
            {initialData && (
              <button type="button"
                onClick={onSuccess}
                className="flex-1 py-3.5 border border-gray-200 rounded-xl font-semibold text-gray-500 hover:bg-gray-50 transition-all"
              >
                Cancel
              </button>
            )}
          </div>
      </form>
    </div>
  );
};

export default RevTargetForm;